<template>
  <div class="salon-app salon-vote">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.vote.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
    </header>

    <main class="salon-main">
      <h2 class="salon-vote-title">{{ $t("salon.vote.heading") }}</h2>
      <p class="salon-vote-lead">
        {{ branchName ? $t("salon.vote.leadAt", { branch: branchName }) : $t("salon.vote.lead") }}
      </p>

      <div class="salon-vote-list">
        <div v-for="member in staff" :key="member.id" class="salon-vote-card">
          <img
            v-if="member.image && !brokenImages.includes(member.id)"
            class="salon-vote-avatar"
            :src="member.image"
            :alt="member.name"
            @error="onImageError(member)"
          />
          <span v-else class="salon-vote-avatar salon-vote-avatar--blank" aria-hidden="true">
            {{ initialOf(member.name) }}
          </span>

          <div class="salon-vote-body">
            <h4 class="salon-vote-name">{{ member.name }}</h4>
            <p v-if="member.job_title" class="salon-vote-role">{{ member.job_title }}</p>

            <!-- The house average, as the booking flow's picker prints it. It is deliberately
                 a number beside the name rather than the stars: the stars below are the
                 member's own ballot, and lighting them from the average would leave them
                 unable to tell what they themselves gave. -->
            <p class="salon-vote-score">
              <template v-if="hasVotes(member)">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                  <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                    fill="currentColor" />
                </svg>
                <span class="salon-vote-average">{{ scoreOf(member) }}</span>
              </template>
              <!-- No star and no number for a stylist nobody has voted for: "0" beside
                   "No votes yet" reads as a score of zero, which is the one thing it is not. -->
              <span class="salon-vote-count">{{ countText(member) }}</span>
            </p>

            <div
              class="salon-vote-stars"
              role="radiogroup"
              :aria-label="$t('salon.vote.yourRatingFor', { name: member.name })"
            >
              <button
                v-for="star in 5"
                :key="star"
                class="salon-vote-star"
                :class="{ 'salon-vote-star--on': star <= starsShown(member) }"
                type="button"
                role="radio"
                :aria-checked="star === Number(member.my_rating || 0)"
                :aria-label="star === 1 ? $t('salon.vote.oneStar') : $t('salon.vote.stars', { count: star })"
                :disabled="savingId === member.id"
                @click="vote(member, star)"
              >
                <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
                  <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                    fill="currentColor" />
                </svg>
              </button>
            </div>

            <!-- One line per card rather than a page-level banner: the customer votes down a
                 list, and a message at the top would not say who it was about. -->
            <p v-if="errorId === member.id" class="salon-vote-note salon-vote-note--error">
              {{ errorMessage }}
            </p>
            <p v-else-if="thanksId === member.id" class="salon-vote-note">{{ $t("salon.vote.thanks") }}</p>
            <p v-else-if="member.my_rating" class="salon-vote-note">
              {{ $t("salon.vote.youRated", { rating: member.my_rating }) }}
            </p>
          </div>
        </div>

        <p v-if="!isLoading && loadFailed" class="salon-empty salon-empty--error">
          {{ $t("salon.vote.loadError") }}
          <button class="salon-retry" type="button" @click="getStaff">{{ $t("salon.bookings.retry") }}</button>
        </p>
        <p v-else-if="!isLoading && !staff.length" class="salon-empty">
          {{ $t("salon.vote.empty") }}
        </p>
      </div>
    </main>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

/**
 * SH044 — "Stylist vote", reached from the avatar menu.
 *
 * Not a step of the booking: a member walks in here to say what they think of the team at the
 * sale channel they are in, whether or not they have an appointment. The roster is therefore
 * the outlet's whole customer-facing roster — the same one the stylist picker draws — with no
 * service narrowing it and nobody greyed out for being busy.
 *
 * Voting is the tap itself; there is no Submit. A list of stylists with one shared button
 * would make the customer say who they meant twice, and the API takes one ballot per stylist
 * and rewrites it, so a mistaken star is fixed by tapping the right one. `my_rating` is that
 * one ballot: a star the customer already left on a past appointment (SH017) lights up here
 * too, because a customer has one opinion of a stylist rather than one per screen.
 */
export default {
  name: "SalonStylistVote",
  components: {
    SalonProfileMenu,
    MaterialLoading,
  },
  props: {
    branchCode: {
      type: String,
      default: "",
    },
    // Named in the lead line, so the customer can see which outlet's team they are rating —
    // the vote is per sale channel, and a chain's stylists differ between them.
    branchName: {
      type: String,
      default: "",
    },
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
  },
  emits: ["back", "profile"],
  data() {
    return {
      isLoading: false,
      loadFailed: false,
      staff: [],
      brokenImages: [],
      // The card whose vote is in flight: its stars are held until the API answers, so a
      // double tap cannot send two different scores.
      savingId: 0,
      // The card that just took a vote, and the one whose vote was refused. Two ids rather
      // than one flag, because the answer belongs beside the stylist it was about.
      thanksId: 0,
      errorId: 0,
      errorMessage: "",
      thanksTimer: null,
    };
  },
  created() {
    this.getStaff();
  },
  beforeUnmount() {
    // The step can be swapped out from under a card that is still saying thank you.
    if (this.thanksTimer) {
      clearTimeout(this.thanksTimer);
    }
  },
  methods: {
    async getStaff() {
      if (!this.branchCode) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await SalonBookingService.getStaffVotes({
          branch_code: this.branchCode,
        });

        if (response.data.result.isSuccess) {
          this.staff = response.data.data.staff ?? [];
        } else {
          this.staff = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // An outlet with no stylists and a broken endpoint both render as an empty list;
        // only one of them deserves a Retry.
        this.staff = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    async vote(member, rating) {
      if (this.savingId) {
        return;
      }

      this.savingId = member.id;
      this.errorId = 0;
      this.errorMessage = "";

      try {
        const response = await SalonBookingService.voteStaff({
          branch_code: this.branchCode,
          staff_id: member.id,
          rating,
        });

        if (response.data.result.isSuccess) {
          // The API hands the card back with the average recomputed, so the score beside the
          // name moves with the vote instead of going stale until the next visit.
          this.applyCard(response.data.data.staff);
          this.sayThanks(member.id);
          return;
        }

        this.showError(member.id, response.data.result.message);
      } catch (error) {
        this.showError(member.id, "");
      } finally {
        this.savingId = 0;
      }
    },

    /**
     * Replace one card in place.
     *
     * splice() rather than assigning to the found object: the stars read `my_rating` off
     * the row, and Vue 3.2 tracks the array element, so swapping the whole row is the
     * change the list actually re-renders on.
     */
    applyCard(card) {
      if (!card) {
        return;
      }

      const at = this.staff.findIndex((member) => member.id === card.id);

      if (at !== -1) {
        this.staff.splice(at, 1, { ...this.staff[at], ...card });
      }
    },

    sayThanks(staffId) {
      this.thanksId = staffId;

      if (this.thanksTimer) {
        clearTimeout(this.thanksTimer);
      }

      // Long enough to be read, short enough that the line goes back to showing the score
      // the customer gave — which is what the card says from then on.
      this.thanksTimer = setTimeout(() => {
        this.thanksId = 0;
        this.thanksTimer = null;
      }, 2500);
    },

    showError(staffId, message) {
      this.thanksId = 0;
      this.errorId = staffId;
      this.errorMessage = message || this.$t("salon.vote.saveError");
    },

    // The stars are the member's own ballot. While one is in flight the card keeps showing
    // the last confirmed score rather than guessing at the answer.
    starsShown(member) {
      return Number(member.my_rating) || 0;
    },

    // The API sends `rating: null` for a stylist nobody has voted for yet.
    hasVotes(member) {
      return (Number(member.rating?.count) || 0) > 0;
    },

    // Number() drops a trailing ".0" on the way out, so a clean average prints as "5"
    // rather than "5.0", as the booking flow's stylist picker prints it.
    scoreOf(member) {
      return String(Number(member.rating?.average) || 0);
    },

    countText(member) {
      const count = Number(member.rating?.count) || 0;

      if (!count) {
        return this.$t("salon.vote.noVotes");
      }

      return count === 1 ? this.$t("salon.vote.oneVote") : this.$t("salon.vote.votes", { count });
    },

    initialOf(name) {
      return (name || "?").trim().charAt(0).toUpperCase();
    },

    onImageError(member) {
      if (!this.brokenImages.includes(member.id)) {
        this.brokenImages.push(member.id);
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-vote {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family, so every heading here names its own face. */
.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  padding: var(--salon-stack-lg) var(--salon-container-padding)
    calc(40px + env(safe-area-inset-bottom, 0px));
}

.salon-vote-title {
  margin: 0 0 var(--salon-stack-sm);
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: var(--salon-on-surface);
}

.salon-vote-lead {
  margin: 0 0 var(--salon-stack-lg);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Stylist cards ─────────────────────────────────────────────── */
.salon-vote-list {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-sm);
}

.salon-vote-card {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-variant);
  border-radius: 12px;
  box-shadow: var(--salon-shadow-soft);
}

.salon-vote-avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 999px;
}

.salon-vote-avatar--blank {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 600;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-variant);
}

.salon-vote-body {
  flex: 1;
  min-width: 0;
}

.salon-vote-name {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-vote-role {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-vote-score {
  display: flex;
  gap: 6px;
  align-items: center;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-secondary-container);
}

.salon-vote-average {
  font-weight: 500;
  color: var(--salon-on-surface);
}

.salon-vote-count {
  color: var(--salon-on-surface-variant);
}

/* ── The ballot ────────────────────────────────────────────────── */
.salon-vote-stars {
  display: flex;
  gap: 4px;
  margin: 10px 0 0;
  margin-left: -4px;
}

.salon-vote-star {
  display: flex;
  padding: 4px;
  /* Unvoted stars are the same grey the cards are ruled in, so "not yet" does not read as
     a bad review. */
  color: var(--salon-surface-container-highest);
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.15s ease;
}

.salon-vote-star--on {
  color: var(--salon-gold);
}

.salon-vote-star:disabled {
  cursor: default;
}

.salon-vote-star:not(:disabled):active {
  transform: scale(0.9);
}

.salon-vote-note {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-vote-note--error {
  color: var(--salon-error);
}

.salon-empty {
  padding: 24px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-empty--error {
  color: var(--salon-error);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}
</style>
